import { BaseSyntheticEvent, ChangeEvent, KeyboardEvent, ReactElement, ReactNode, RefObject, useCallback } from 'react';
import { KeyValue } from '@mews/enums';
import { InputValue } from '@optimus-web/common';
import { SelectBaseProps } from '../types';
import { flattenChildren, isOptionGroup } from './useFilteredItems';

interface KeyboardNavigationState {
    opened: boolean;
    selectedIndex: number;
    searchValue: string;
}

interface KeyboardNavigationHandlers {
    open: () => void;
    close: () => void;
    setSelectedIndex: (index: number | ((prev: number) => number)) => void;
    setIndexChangeReason: (reason: 'user' | 'internal') => void;
    setKeyboardHandled: (handled: boolean) => void;
    handleSelectAllAction: (e: BaseSyntheticEvent) => void;
    handleItemClick: (e: BaseSyntheticEvent, value: InputValue) => void;
    handleListFocus?: () => void;
}

interface KeyboardNavigationComputed {
    filteredAndMappedItems: ReactElement[];
    selectAll: boolean;
}

interface UseKeyboardNavigationProps
    extends Pick<SelectBaseProps, 'readOnly' | 'multiselect' | 'disabled' | 'selected' | 'onChange' | 'footer'>,
        KeyboardNavigationState,
        KeyboardNavigationHandlers,
        KeyboardNavigationComputed {
    inputRef?: RefObject<HTMLInputElement>;
}

// Helper for initializing properly the selectedIndex state variable
export const findFirstSelectableIndex = (children: ReactNode, selectAll: boolean): number => {
    const flattenedItems = flattenChildren(children);

    // Find first non-group item
    const firstSelectableIndex = flattenedItems.findIndex((item) => !isOptionGroup(item));

    // If no selectable items found, return appropriate default
    if (firstSelectableIndex === -1) return selectAll ? 1 : 0;

    // If selectAll is enabled, offset the index by 1
    return selectAll ? firstSelectableIndex + 1 : firstSelectableIndex;
};

// Helper handling selected index for select-all and grouping cases
const findNextValidIndex = (
    direction: 1 | -1,
    currentIndex: number,
    filteredAndMappedItems: KeyboardNavigationComputed['filteredAndMappedItems'],
    selectAll: KeyboardNavigationComputed['selectAll']
): number => {
    if (!selectAll) {
        // Handle non-select-all cases (no changes needed here)
        const itemsLength = filteredAndMappedItems.length;
        let nextIndex = currentIndex;
        do {
            if (direction === 1) {
                nextIndex = nextIndex === itemsLength - 1 ? 0 : nextIndex + 1;
            } else {
                nextIndex = nextIndex === 0 ? itemsLength - 1 : nextIndex - 1;
            }
            if (nextIndex === currentIndex) return currentIndex;
        } while (isOptionGroup(filteredAndMappedItems[nextIndex]));
        return nextIndex;
    }

    if (direction === -1) {
        // Moving up
        // First, handle the case of moving up from first real item
        let firstNonGroupIndex = 1;
        while (
            firstNonGroupIndex < filteredAndMappedItems.length &&
            isOptionGroup(filteredAndMappedItems[firstNonGroupIndex - 1])
        ) {
            firstNonGroupIndex++;
        }

        if (currentIndex === firstNonGroupIndex) {
            return 0; // Go to select-all
        }

        let nextIndex = currentIndex - 1;
        // Skip group headers when moving up
        while (nextIndex >= firstNonGroupIndex && isOptionGroup(filteredAndMappedItems[nextIndex - 1])) {
            nextIndex--;
        }

        // If we went past the beginning, wrap to the end
        if (nextIndex < firstNonGroupIndex) {
            nextIndex = filteredAndMappedItems.length;
            while (nextIndex > firstNonGroupIndex && isOptionGroup(filteredAndMappedItems[nextIndex - 1])) {
                nextIndex--;
            }
        }
        return nextIndex;
    }

    if (currentIndex === 0) {
        // From select all to first non-group item
        let nextIndex = 1;
        while (nextIndex < filteredAndMappedItems.length && isOptionGroup(filteredAndMappedItems[nextIndex - 1])) {
            nextIndex++;
        }
        return nextIndex;
    }

    let nextIndex = currentIndex + 1;
    // Skip group headers when moving down
    while (nextIndex <= filteredAndMappedItems.length && isOptionGroup(filteredAndMappedItems[nextIndex - 1])) {
        nextIndex++;
    }

    // If we went past the end, wrap to select all
    if (nextIndex > filteredAndMappedItems.length) {
        return 0;
    }
    return nextIndex;
};

export const useKeyboardNavigation = ({
    opened,
    readOnly,
    filteredAndMappedItems,
    selectAll,
    selectedIndex,
    multiselect,
    disabled,
    searchValue,
    selected,
    onChange,
    open,
    close,
    setSelectedIndex,
    setIndexChangeReason,
    setKeyboardHandled,
    handleSelectAllAction,
    handleItemClick,
    handleListFocus,
    footer,
    inputRef,
}: UseKeyboardNavigationProps) => {
    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLElement>) => {
            if (readOnly || !filteredAndMappedItems.length) return;

            let handled = false;

            // Count only selectable items
            const selectableItemCount = filteredAndMappedItems.reduce((count, item) => {
                if (isOptionGroup(item)) return count;
                return count + 1;
            }, 0);

            const totalItemsCount = selectAll ? selectableItemCount + 1 : selectableItemCount;

            if (e.key === KeyValue.ArrowDown) {
                if (opened && totalItemsCount > 0) {
                    const nextIndex = findNextValidIndex(1, selectedIndex, filteredAndMappedItems, selectAll);
                    setSelectedIndex(nextIndex);
                    setIndexChangeReason('user');
                    handled = true;
                }
                if (!opened) {
                    open();
                    handled = true;
                }
            } else if (e.key === KeyValue.ArrowUp) {
                if (opened && totalItemsCount > 0) {
                    const nextIndex = findNextValidIndex(-1, selectedIndex, filteredAndMappedItems, selectAll);
                    setSelectedIndex(nextIndex);
                    setIndexChangeReason('user');
                    handled = true;
                }
                if (!opened) {
                    open();
                    handled = true;
                }
            } else if (e.key === KeyValue.Enter) {
                if (opened && totalItemsCount > 0) {
                    if (selectAll && selectedIndex === 0) {
                        handleSelectAllAction(e);
                        handled = true;
                    } else {
                        // Note: we adjust the index because index 0 is "Select All" in multiselect mode
                        const menuItem = filteredAndMappedItems[selectAll ? selectedIndex - 1 : selectedIndex];
                        if (!menuItem.props.disabled) {
                            handleItemClick(e, menuItem.props.value);
                        }
                        handled = true;
                    }
                }
            } else if (e.key === KeyValue.Escape) {
                if (opened) {
                    close();
                    handled = true;
                }
            } else if (e.key === KeyValue.Tab) {
                if (opened) {
                    if (!multiselect && !footer && totalItemsCount > 0) {
                        // For single-select with no footer, select current item and close
                        const menuItem = filteredAndMappedItems[selectedIndex];
                        if (!menuItem.props.disabled) {
                            handleItemClick(e, menuItem.props.value);
                        }
                        close();
                        handled = true;
                    } else if (inputRef?.current === e.target && handleListFocus) {
                        // If we're tabbing from the input field, move focus to the list
                        e.preventDefault();
                        handleListFocus();
                        handled = true;
                    }
                }
            } else if (e.key === KeyValue.Backspace) {
                if (multiselect && !disabled) {
                    if (!searchValue && Array.isArray(selected) && selected.length > 0) {
                        const newSelected = [...selected];
                        newSelected.pop();
                        onChange?.(e as unknown as ChangeEvent, newSelected);
                        handled = true;
                    }
                }
            }

            if (handled) {
                e.preventDefault();
                e.stopPropagation();
                setKeyboardHandled(true);
            }
        },
        [
            filteredAndMappedItems,
            selectAll,
            opened,
            open,
            selectedIndex,
            handleSelectAllAction,
            handleItemClick,
            close,
            multiselect,
            disabled,
            searchValue,
            selected,
            onChange,
            readOnly,
            setSelectedIndex,
            setIndexChangeReason,
            setKeyboardHandled,
            handleListFocus,
            footer,
            inputRef,
        ]
    );

    return { handleKeyDown };
};
